// src/bookings/bookingsApi.ts
import { apiClient } from "@maat/api-base";
var bookingsApi = {
  validateCheckout: async (data) => {
    return apiClient.post("/booking/api/checkout/validate", data);
  },
  createHold: async (data) => {
    return apiClient.post("/booking/api/bookings/hold", data);
  },
  /**
   * The SAME endpoint as `createHold`, with the session-shaped body: a stay
   * names NIGHTS, a session CELLS, and they are MUTUALLY EXCLUSIVE — a body
   * carrying both is a 400. Two methods rather than a union payload, which
   * would let a caller send `check_in` beside `slot_ids` and learn from the
   * server. Handle the 409s: `SLOT_UNAVAILABLE` carries fresh per-cell
   * `remaining`, `SLOT_RUN_*` say the run is no longer bookable.
   */
  createSlotHold: async (data) => {
    return apiClient.post("/booking/api/bookings/hold", data);
  },
  releaseHold: async (holdId) => {
    return apiClient.post(`/booking/api/bookings/hold/${holdId}/release`, {});
  },
  // Guest transparency — public.
  getListingConstraints: async (listingId) => {
    return apiClient.get(`/booking/api/listings/${listingId}/constraints`);
  },
  evaluateBooking: async (listingId, data) => {
    return apiClient.post(`/booking/api/listings/${listingId}/evaluate`, data);
  },
  // The calendar's source of truth for which checkout dates the rules reject.
  getCheckoutAvailability: async (listingId, params) => {
    return apiClient.get(`/booking/api/listings/${listingId}/checkout-availability`, {
      params
    });
  },
  confirmBooking: async (bookingId) => {
    return apiClient.post(`/booking/api/bookings/${bookingId}/confirm`, {});
  },
  rejectBooking: async (bookingId) => {
    return apiClient.post(`/booking/api/bookings/${bookingId}/reject`, {});
  },
  /** Move a booking to another room of the same room type (same dates, same price). */
  reassignUnit: async (bookingId, unitId) => {
    return apiClient.post(`/booking/api/bookings/${bookingId}/reassign-unit`, {
      unit_id: unitId
    });
  },
  checkInBooking: async (bookingId) => {
    return apiClient.post(`/booking/api/bookings/${bookingId}/checkin`, {});
  },
  checkOutBooking: async (bookingId) => {
    return apiClient.post(`/booking/api/bookings/${bookingId}/checkout`, {});
  },
  listBookings: async (params) => {
    return apiClient.get("/booking/api/bookings", { params });
  },
  getBooking: async (bookingId) => {
    return apiClient.get(`/booking/api/bookings/${bookingId}`);
  },
  /** Host (`bookings.view`): this workspace's history with the booking's guest (README H24). */
  getGuestIntel: async (bookingId) => {
    return apiClient.get(`/booking/api/bookings/${bookingId}/guest-intel`);
  },
  // Admin: list any bookings across workspaces (admin-token gated).
  adminListBookings: async (params) => {
    return apiClient.get("/booking/api/bookings/admin", {
      params,
      paramsSerializer: { indexes: null }
    });
  },
  // Admin: any booking by id (admin-token gated) — used by the admin payout detail.
  adminGetBooking: async (bookingId) => {
    return apiClient.get(`/booking/api/bookings/admin/${bookingId}`);
  },
  // Admin: booking + revenue stats for one workspace (admin-token gated).
  adminGetWorkspaceBookingStats: async (workspaceId) => {
    return apiClient.get(`/booking/api/bookings/admin/workspace-stats/${workspaceId}`);
  },
  // Admin: monthly financial time-series (GMV/earnings) for the workspace Finance tab.
  adminGetWorkspaceFinancialTimeSeries: async (workspaceId, months) => {
    return apiClient.get(
      `/booking/api/bookings/admin/workspace-stats/${workspaceId}/timeseries`,
      { params: { months } }
    );
  },
  // Admin: monthly bookings-by-status time-series for the workspace Bookings tab.
  adminGetWorkspaceBookingTimeSeries: async (workspaceId, months) => {
    return apiClient.get(
      `/booking/api/bookings/admin/workspace-stats/${workspaceId}/bookings-timeseries`,
      { params: { months } }
    );
  },
  // Admin: lifetime guest-spend stats for one user (admin user-detail Finance tab).
  adminGetUserSpendStats: async (userId) => {
    return apiClient.get(`/booking/api/bookings/admin/user-stats/${userId}`);
  },
  // Admin: monthly guest-spend time-series for the admin user Finance tab chart.
  adminGetUserSpendTimeSeries: async (userId, months) => {
    return apiClient.get(`/booking/api/bookings/admin/user-stats/${userId}/timeseries`, {
      params: { months }
    });
  },
  // Admin: monthly bookings-by-status time-series for the user / listing Bookings tabs.
  adminGetUserBookingTimeSeries: async (userId, months) => {
    return apiClient.get(
      `/booking/api/bookings/admin/user-stats/${userId}/bookings-timeseries`,
      { params: { months } }
    );
  },
  adminGetListingBookingTimeSeries: async (listingId, months) => {
    return apiClient.get(
      `/booking/api/bookings/admin/listing-stats/${listingId}/bookings-timeseries`,
      { params: { months } }
    );
  },
  // Platform-wide admin dashboard aggregates — admin-token gated.
  adminGetPlatformBookingStats: async () => {
    return apiClient.get("/booking/api/bookings/admin/platform-stats");
  },
  adminGetPlatformFinancialTimeSeries: async (months) => {
    return apiClient.get("/booking/api/bookings/admin/platform-stats/financial-timeseries", {
      params: { months }
    });
  },
  adminGetPlatformBookingTimeSeries: async (months) => {
    return apiClient.get("/booking/api/bookings/admin/platform-stats/bookings-timeseries", {
      params: { months }
    });
  },
  adminGetTopWorkspaces: async (limit = 10) => {
    return apiClient.get("/booking/api/bookings/admin/top-workspaces", { params: { limit } });
  },
  adminGetTopListings: async (limit = 10) => {
    return apiClient.get("/booking/api/bookings/admin/top-listings", { params: { limit } });
  },
  getListingStats: async (listingId) => {
    return apiClient.get(`/booking/api/listings/${listingId}/stats`);
  },
  listMyBookings: async (params) => {
    return apiClient.get("/booking/api/my-bookings", { params });
  },
  getMyBooking: async (bookingId) => {
    return apiClient.get(`/booking/api/my-bookings/${bookingId}`);
  },
  // Resolve a hold to the guest's booking — long-polled after a payment
  // redirect. `{ booking: null }` until it is created.
  resolveMyBookingByHold: async (holdId) => {
    return apiClient.get(`/booking/api/my-bookings/by-hold/${holdId}`);
  },
  // The guest's unfinished checkouts — one resumable hold per listing.
  getUnfinishedCheckouts: async (params) => {
    return apiClient.get("/booking/api/my-holds/unfinished", { params });
  },
  // Guest cancels their own booking (resolves the booking's selected policy).
  cancelMyBooking: async (bookingId, data = {}) => {
    return apiClient.post(`/booking/api/my-bookings/${bookingId}/cancel`, data);
  },
  // Host cancels a booking in their workspace (resolves the fixed host policy).
  cancelBooking: async (bookingId, data = {}) => {
    return apiClient.post(`/booking/api/bookings/${bookingId}/cancel`, data);
  },
  // Public cancellation policy catalogue (with stage tiers, both personas).
  listCancellationPolicies: async () => {
    return apiClient.get("/booking/api/cancellation-policies");
  },
  createRule: async (data) => {
    return apiClient.post("/booking/api/rules", data);
  },
  // Gen-AI authoring: NL description → validated draft, NOT persisted (the host
  // saves it via createRule). `rule_name`/`trigger` are not returned — the
  // builder derives them from the graph on save.
  generateRule: async (description) => {
    return apiClient.post("/booking/api/rules/generate", { description });
  },
  // Gen-AI template authoring (admin_token gated) — `generateRule`'s draft
  // shape, no workspace; saved via createRuleTemplate.
  adminGenerateRuleTemplate: async (description) => {
    return apiClient.post("/booking/api/rule-templates/generate", { description });
  },
  // Hand-authored rules ONLY — quick-action rules belong to their listing and
  // are managed there. `quick_rule_count` rides along for the empty state.
  listRules: async () => {
    return apiClient.get("/booking/api/rules");
  },
  getRule: async (ruleId) => {
    return apiClient.get(`/booking/api/rules/${ruleId}`);
  },
  updateRule: async (ruleId, data) => {
    return apiClient.put(`/booking/api/rules/${ruleId}`, data);
  },
  deleteRule: async (ruleId) => {
    return apiClient.delete(`/booking/api/rules/${ruleId}`);
  },
  linkRuleToListing: async (listingId, data) => {
    return apiClient.post(`/booking/api/listings/${listingId}/rules`, data);
  },
  unlinkRuleFromListing: async (listingId, ruleId) => {
    return apiClient.delete(`/booking/api/listings/${listingId}/rules/${ruleId}`);
  },
  toggleRuleForListing: async (listingId, ruleId, isActive) => {
    return apiClient.patch(`/booking/api/listings/${listingId}/rules/${ruleId}/active`, {
      is_active: isActive
    });
  },
  listLinkedRules: async (listingId) => {
    return apiClient.get(`/booking/api/listings/${listingId}/rules`);
  },
  getRuleLinkedListings: async (ruleId) => {
    return apiClient.get(`/booking/api/rules/${ruleId}/listings`);
  },
  batchLinkListings: async (ruleId, listingIds) => {
    return apiClient.post(`/booking/api/rules/${ruleId}/listings`, { listing_ids: listingIds });
  },
  batchUnlinkListings: async (ruleId, listingIds) => {
    return apiClient.delete(`/booking/api/rules/${ruleId}/listings`, {
      data: { listing_ids: listingIds }
    });
  },
  simulateRule: async (ruleId, data) => {
    return apiClient.post(`/booking/api/rules/${ruleId}/simulate`, data);
  },
  // Blueprints a host can "start from" when creating a rule. Published only.
  listPublishedRuleTemplates: async () => {
    return apiClient.get("/booking/api/rule-templates/published");
  },
  getRuleTemplate: async (templateId) => {
    return apiClient.get(`/booking/api/rule-templates/${templateId}`);
  },
  // Seeded templates as one-tap controls on the listing create/edit flows.
  // Unlike "start from template", applying one is a SERVER-side instantiation —
  // the clients never touch the AST, which is what lets native offer them.
  listRuleQuickActions: async (listingId) => {
    return apiClient.get("/booking/api/rule-templates/quick-actions", {
      params: listingId ? { listing_id: listingId } : void 0
    });
  },
  applyRuleQuickAction: async (templateKey, data) => {
    return apiClient.post(
      `/booking/api/rule-templates/quick-actions/${templateKey}/apply`,
      data
    );
  },
  removeRuleQuickAction: async (templateKey, listingId) => {
    return apiClient.delete(`/booking/api/rule-templates/quick-actions/${templateKey}`, {
      params: { listing_id: listingId }
    });
  },
  // Rule template authoring — admin-token gated.
  adminListRuleTemplates: async () => {
    return apiClient.get("/booking/api/rule-templates");
  },
  adminCreateRuleTemplate: async (data) => {
    return apiClient.post("/booking/api/rule-templates", data);
  },
  adminUpdateRuleTemplate: async (templateId, data) => {
    return apiClient.put(`/booking/api/rule-templates/${templateId}`, data);
  },
  adminDeleteRuleTemplate: async (templateId) => {
    return apiClient.delete(`/booking/api/rule-templates/${templateId}`);
  },
  createBookingInvitation: async (data) => {
    return apiClient.post("/booking/api/booking-invitations", data);
  },
  cancelBookingInvitation: async (invitationId) => {
    return apiClient.post(`/booking/api/booking-invitations/${invitationId}/cancel`, {});
  },
  listBookingInvitations: async (params) => {
    return apiClient.get("/booking/api/booking-invitations", { params });
  },
  listMyBookingInvitations: async (params) => {
    return apiClient.get("/booking/api/my-booking-invitations", { params });
  },
  declineBookingInvitation: async (invitationId) => {
    return apiClient.post(`/booking/api/my-booking-invitations/${invitationId}/decline`, {});
  },
  prepareInvitationCheckout: async (invitationId) => {
    return apiClient.post(
      `/booking/api/my-booking-invitations/${invitationId}/prepare-checkout`,
      {}
    );
  },
  getBookingInvitation: async (invitationId) => {
    return apiClient.get(`/booking/api/booking-invitations/${invitationId}`);
  },
  requestBookingModification: async (data) => {
    return apiClient.post("/booking/api/my-booking-modifications", data);
  },
  listMyBookingModifications: async (params) => {
    return apiClient.get("/booking/api/my-booking-modifications", { params });
  },
  cancelMyBookingModification: async (modificationId) => {
    return apiClient.post(`/booking/api/my-booking-modifications/${modificationId}/cancel`, {});
  },
  prepareModificationCheckout: async (modificationId) => {
    return apiClient.post(
      `/booking/api/my-booking-modifications/${modificationId}/prepare-checkout`,
      {}
    );
  },
  /** Guest approves an ADMIN-initiated unit change — applies immediately. */
  approveMyBookingModification: async (modificationId) => {
    return apiClient.post(
      `/booking/api/my-booking-modifications/${modificationId}/approve`,
      {}
    );
  },
  /** Guest declines an ADMIN-initiated unit change — the target unit is released. */
  declineMyBookingModification: async (modificationId) => {
    return apiClient.post(
      `/booking/api/my-booking-modifications/${modificationId}/decline`,
      {}
    );
  },
  /** Admin: full modification history for one booking (newest first). */
  adminListBookingModifications: async (bookingId) => {
    return apiClient.get(`/booking/api/bookings/admin/${bookingId}/modifications`);
  },
  /** Admin previews moving a booking to another listing (re-quote, no writes). */
  adminUnitChangePreview: async (bookingId, data) => {
    return apiClient.post(`/booking/api/bookings/admin/${bookingId}/unit-change/preview`, data);
  },
  /** Admin creates the unit change (guest must approve before it applies). */
  adminCreateUnitChange: async (bookingId, data) => {
    return apiClient.post(`/booking/api/bookings/admin/${bookingId}/unit-change`, data);
  },
  /** Host offers an extension (added nights) on a guest's booking. */
  createBookingModification: async (data) => {
    return apiClient.post("/booking/api/booking-modifications", data);
  },
  approveBookingModification: async (modificationId) => {
    return apiClient.post(`/booking/api/booking-modifications/${modificationId}/approve`, {});
  },
  declineBookingModification: async (modificationId) => {
    return apiClient.post(`/booking/api/booking-modifications/${modificationId}/decline`, {});
  },
  listBookingModifications: async (params) => {
    return apiClient.get("/booking/api/booking-modifications", { params });
  },
  getBookingModification: async (modificationId) => {
    return apiClient.get(`/booking/api/booking-modifications/${modificationId}`);
  }
};

export {
  bookingsApi
};
